import { useEffect, useMemo, useState, useCallback, useRef } from 'react'

import { useQuery } from '@apollo/client'
import { message } from 'antd'

import { GET_PUBLIC_PLATFORMACTION_SETTINGS } from '../settings/graphQL'
import { GET_TEMPLATE_RESPONSE } from '../templates/graphQL'
import { getBlockFromTemplateData } from '../templates/templateRenderer/utils/getBlockFromTemplateData'
import { getTemplateResponseSummary } from '../templates/templateRenderer/utils/getTemplateResponseSummary'

import { AIButton } from '@/components/shared-components/AIButton'
import { useAIActivity } from '@/context/aiActivityContext'
import { useRegisterAIToolUsage } from '@/hooks/useRegisterAIToolUsage'

import {
  PublicPlatformActionSettingsUiQuery,
  TemplateResponseUiQuery,
  TemplateResponseUiQueryVariables,
} from 'types/api.graphql'
import { PublicPlatformActionSettingsUiQueryVariables } from 'types/api.graphql'

type ActionDataGenerated = {
  title: string
  description: string
  priority: string
  type: string
}

type Props = {
  builderId: string
  captureBlockId: string
  onSuccess: (actionDataGenerated: ActionDataGenerated) => void
  templateResponseId?: string
}

export const ActionAIGenerator: React.FC<Props> = ({
  builderId,
  captureBlockId,
  templateResponseId,
  onSuccess,
}) => {
  const { isAIRunning, setAIRunning } = useAIActivity()
  const [prefetchData, setPrefetchData] = useState<ActionDataGenerated | null>(
    null
  )
  const fetchPromiseRef = useRef<Promise<void> | null>(null)
  const { registerUsage } = useRegisterAIToolUsage('actionGenerator')

  const { data: settingsData, loading: settingsLoading } = useQuery<
    PublicPlatformActionSettingsUiQuery,
    PublicPlatformActionSettingsUiQueryVariables
  >(GET_PUBLIC_PLATFORMACTION_SETTINGS, { variables: { builderId } })

  const { data: templateResponseData, loading: templateResponseLoading } =
    useQuery<TemplateResponseUiQuery, TemplateResponseUiQueryVariables>(
      GET_TEMPLATE_RESPONSE,
      {
        variables: {
          id: templateResponseId,
        },
        skip: !templateResponseId,
      }
    )

  const { targetBlock, customPrompt, extraContext } = useMemo(() => {
    if (!templateResponseData)
      return {
        targetBlock: undefined,
        customPrompt:
          'Return an example action. Preface the title with EXAMPLE.',
        extraContext: undefined,
      }

    const blockResponse = getBlockFromTemplateData(
      templateResponseData?.templateResponse?.responseData,
      captureBlockId
    )

    const blockTemplate = getBlockFromTemplateData(
      templateResponseData?.templateResponse?.template?.templateData,
      captureBlockId
    )

    return {
      targetBlock: blockResponse,
      customPrompt: blockTemplate?.platformActionsPrompt,
      extraContext: getTemplateResponseSummary(
        templateResponseData?.templateResponse?.responseData
      ),
    }
  }, [templateResponseData, captureBlockId])

  const { types, priorities } = useMemo(() => {
    return {
      types:
        settingsData?.publicPlatformActionSettings?.types.map(
          (type) => type.label
        ) || [],
      priorities:
        settingsData?.publicPlatformActionSettings?.priorities.map(
          (priority) => priority.label
        ) || [],
    }
  }, [settingsData])

  const fetchData = useCallback(
    async (assignImmediately = false) => {
      if (assignImmediately) setAIRunning(true)

      const promise = (async () => {
        try {
          // Wait for settings to load with max 5 retries
          let retries = 0
          while (
            !assignImmediately &&
            (settingsLoading || templateResponseLoading) &&
            retries < 5
          ) {
            await new Promise((resolve) => setTimeout(resolve, 1000))
            retries++
          }

          const response = await fetch('/api/ai/generateActionFromContext', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              types,
              priorities,
              context: targetBlock,
              customPrompt,
              extraContext,
            }),
          })

          if (!response.ok) {
            throw new Error(`API request failed: ${response.statusText}`)
          }

          const actionData = (await response.json()) as ActionDataGenerated

          // If assignImmediately is true, call onSuccess directly
          // Otherwise store in prefetchData for later use
          if (assignImmediately) {
            onSuccess?.(actionData)
          } else {
            setPrefetchData(actionData)
          }
        } catch (error) {
          console.error('Error filling with AI', error)
          throw error
        } finally {
          if (assignImmediately) setAIRunning(false)
          fetchPromiseRef.current = null
        }
      })()

      fetchPromiseRef.current = promise
      return promise
    },
    [
      setAIRunning,
      settingsLoading,
      templateResponseLoading,
      types,
      priorities,
      targetBlock,
      customPrompt,
      extraContext,
      onSuccess,
    ]
  )

  const handleFillWithAI = async () => {
    try {
      setAIRunning(true)

      registerUsage('Generated action from checklist context')

      // Case 1: Use existing prefetched data if available
      if (prefetchData) {
        // Add a 1 second delay while showing loading state
        setAIRunning(true)
        await new Promise((resolve) => setTimeout(resolve, 300))
        setAIRunning(false)

        onSuccess(prefetchData)
        setPrefetchData(null)
        return
      }

      // Case 2: Wait for ongoing fetch to complete if exists
      if (fetchPromiseRef.current) {
        await fetchPromiseRef.current
        // Check prefetchData again after waiting
        if (prefetchData) {
          onSuccess(prefetchData)
          setPrefetchData(null)
          return
        }
      }

      // Case 3: No prefetched data available, fetch new data
      await fetchData(true)
    } catch (error) {
      console.error('Error filling with AI:', error)
      message.error('Failed to generate action. Please try again.')
    } finally {
      setAIRunning(false)
    }
  }

  // Prefetch on mount
  useEffect(() => {
    fetchData(false)
  }, [fetchData])

  return (
    <AIButton
      onClick={handleFillWithAI}
      loading={isAIRunning}
      variant="secondary"
    >
      Generate
    </AIButton>
  )
}
